/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * AiReport.jsx — สรุปค่าใช้จ่าย AI + ตั้งโควตา   (งานข้อ 4 · Step 5.4)
 *
 * เมนูใหม่ในกลุ่มตั้งค่าระบบ (menus.code = ai_report — migration 0009)
 * ตอบ 3 อย่าง: เดือนนี้ใช้เท่าไหร่ · ย้อนหลังเป็นยังไง · ปรับเพดาน/หยุดฉุกเฉินตรงไหน
 *
 * สิทธิ์แก้ตั้งค่า = ai_setting (backend เช็คซ้ำเสมอ — ฝั่งจอแค่ซ่อนฟอร์ม)
 * ตัวเลขบาทเป็น "ประมาณการ" จากอัตรา baht_per_credit ของ backend — ของจริงดูใบแจ้งหนี้ผู้ให้บริการ
 */

import React, { useEffect, useRef, useState } from 'react';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import AiQuotaPanel from './AiQuotaPanel'; // โควตารายยศ/รายคน (ตรวจการใช้งาน 2.2 · 1 พ.ย. 2569)

/* รหัส error ตอนบันทึกตั้งค่า → ข้อความไทย (16 ต.ค. 2569 — ช่องว่างเคยกลายเป็น 0 แล้วปิด AI ทั้งร้านเงียบ ๆ) */
const SAVE_ERROR_TEXT = {
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์แก้การตั้งค่า AI',
  ai_quota_not_numeric: 'โควตาต้องเป็นจำนวนเต็ม 0 ขึ้นไป (ใส่ 0 = ปิด AI)',
  ai_quota_invalid: 'โควตาต้องอยู่ระหว่าง 0–100,000 ภาพ',
  ai_budget_not_numeric: 'งบต่อเดือนต้องเป็นตัวเลข (ใส่ 0 ได้)',
  ai_budget_invalid: 'งบต่อเดือนต้องอยู่ระหว่าง 0–10,000,000 บาท',
};

const CREDIT_ERROR = 'เช็คเครดิตไม่สำเร็จ · เชื่อมต่อผู้ให้บริการไม่ได้';

/* "2026-10" → "ต.ค. 2026" — ปี ค.ศ. ให้ตรงกับวันที่ทุกหน้าของ ERP (Func.dateTimeDisplay) */
const TH_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];
const monthText = (ym) => {
  const [y, m] = String(ym || '')
    .split('-')
    .map(Number);
  return y > 0 && m >= 1 && m <= 12 ? `${TH_MONTHS[m - 1]} ${y}` : ym;
};

const initSettings = {
  ai_monthly_quota: 0,
  ai_monthly_budget: 0,
  ai_over_quota: 'STOP',
  ai_kill_switch: 'N',
};

function AiReport() {
  const mod = 'ai_report';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();

  const [month, setMonth] = useState(null);
  const [byProvider, setByProvider] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyState, setHistoryState] = useState('loading');
  const [settings, setSettings] = useState(initSettings); // ค่าในฟอร์ม (ยังไม่บันทึก)
  const [saved, setSaved] = useState(initSettings); // ค่าที่บันทึกแล้วจริง — การ์ดสรุปอ่านจากตัวนี้
  const [confirmOff, setConfirmOff] = useState([]); // เหตุผลที่บันทึกนี้จะปิด AI ทั้งร้าน
  const [confirmOpen, setConfirmOpen] = useState(false);
  const notifyTimer = useRef(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false); // โหลดค่าตั้งค่าสำเร็จแล้ว — ไม่มีสิทธิ์ต้องไม่โชว์ "AI ปิดทั้งร้าน" จากค่าว่าง (รีเช็ค 27 ต.ค. 2569)
  const [credit, setCredit] = useState(null); // เครดิตคงเหลือจริง (โหลดเมื่อกดปุ่ม)
  const [bahtPerCredit, setBahtPerCredit] = useState(0); // อัตราจาก backend (init.baht_per_credit) ไม่ฝังเลขในจอ
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );

    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  const loadHistory = () => {
    setHistoryState('loading');
    FetchData('POST', `/${mod}`, app.token, { _t: Date.now() })
      .then((view) => {
        if (!alive.current) return;
        setHistory(view.status === 'ok' ? view.data || [] : []);
        setHistoryState(view.status === 'ok' ? 'done' : 'error');
      })
      .catch(() => {
        if (alive.current) {
          setHistory([]);
          setHistoryState('error');
        }
      });
  };

  const load = () => {
    setLoading(true);
    loadHistory();
    FetchData('GET', `/${mod}`, app.token, {})
      .then((init) => {
        if (!alive.current) return;
        setLoading(false);

        if (init.status === 'ok') {
          setMonth(init.month);
          setByProvider(init.by_provider || []);
          setSettings({ ...initSettings, ...init.settings });
          setSaved({ ...initSettings, ...init.settings });
          setLoaded(true);
          setCanEdit(init.can_edit === 'Y');
          setBahtPerCredit(Number(init.baht_per_credit) || 0);
        } else if (init.msg === 'permission_denied') {
          showNotify('บัญชีนี้ไม่มีสิทธิ์ดูค่าใช้จ่าย AI', 'error');
        } else {
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(t(`alert.loaderror`), 'error');
        }
      });
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    load();
  }, []);

  const loadCredit = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/credit`, app.token, { _t: Date.now() })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          setCredit(result);
        } else if (result.msg === 'permission_denied') {
          showNotify('บัญชีนี้ไม่มีสิทธิ์ดูค่าใช้จ่าย AI', 'error');
        } else {
          showNotify(CREDIT_ERROR, 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(CREDIT_ERROR, 'error');
        }
      });
  };

  const saveSettings = () => {
    setConfirmOpen(false);
    setLoading(true);

    FetchData('PATCH', `/${mod}/settings`, app.token, {
      ai_monthly_quota: settings.ai_monthly_quota,
      ai_monthly_budget: settings.ai_monthly_budget,
      ai_over_quota: settings.ai_over_quota,
      ai_kill_switch: settings.ai_kill_switch,
    })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          showNotify(t(`alert.savecomplete`), 'success');
          const next = { ...initSettings, ...(result.settings || settings) };
          setSettings(next);
          setSaved(next);
        } else {
          showNotify(SAVE_ERROR_TEXT[result.msg] || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(t(`alert.saveerror`), 'error');
        }
      });
  };

  /* บันทึกที่ทำให้ AI ปิดทั้งร้าน (โควตา >0 → 0 · เปิดสวิตช์หยุดฉุกเฉิน) ต้องยืนยันก่อน (18 ต.ค. 2569) */
  const quotaZero = (v) => String(v).trim() !== '' && Number(v) === 0;
  const requestSave = () => {
    const reasons = [];
    if (quotaZero(settings.ai_monthly_quota) && !quotaZero(saved.ai_monthly_quota)) {
      reasons.push('โควตา 0 ภาพ');
    }
    if (settings.ai_kill_switch === 'Y' && saved.ai_kill_switch !== 'Y') {
      reasons.push('หยุดฉุกเฉิน');
    }
    if (reasons.length) {
      setConfirmOff(reasons);
      setConfirmOpen(true);
    } else {
      saveSettings();
    }
  };

  const num = (v) => Func.numberDisplay(v || 0, 0);
  const baht = (v) => Func.numberDisplay(v || 0, 2);

  const summaryCard = (label, value, caption, color) => (
    <Grid item xs={6} md={3}>
      <Card sx={{ p: 2, height: '100%' }}>
        <MDTypography variant='caption' color='text'>
          {label}
        </MDTypography>
        <MDTypography variant='h4' color={color || 'dark'}>
          {value}
        </MDTypography>
        {caption && (
          <MDTypography variant='caption' color='text'>
            {caption}
          </MDTypography>
        )}
      </Card>
    </Grid>
  );

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.setting`), 'AI · ค่าใช้จ่ายและโควตา']} />

      <MDBox my={2}>
        <Grid container spacing={2}>
          {/* ── สรุปเดือนนี้ ─────────────────────────────────────────── */}
          {month && (
            <>
              {summaryCard(
                'ภาพที่สร้างเดือนนี้',
                num(month.images),
                `สินค้า ${num(month.product_images)} · แคมเปญ ${num(month.campaign_images)}`
              )}
              {summaryCard(
                'เครดิตที่ใช้เดือนนี้',
                num(month.credits),
                `≈ ${baht(month.est_baht)} บาท (ประมาณการ)`
              )}
              {/* โควตา 0 = ระบบไม่ยิง AI เลย (ai_quota_not_set) ไม่ใช่ "ยังไม่ตั้ง" */}
              {Number(saved.ai_monthly_quota) > 0
                ? summaryCard(
                    'โควตาเดือนนี้',
                    `${num(month.images)}/${num(saved.ai_monthly_quota)}`,
                    saved.ai_over_quota === 'STOP'
                      ? 'เกินแล้วหยุดสร้าง'
                      : 'เกินแล้วเตือนแต่สร้างต่อได้',
                    month.images >= Number(saved.ai_monthly_quota) ? 'error' : 'dark'
                  )
                : summaryCard('โควตาเดือนนี้', 'AI ปิดทั้งร้าน', 'โควตาเป็น 0 ภาพ', 'error')}
              <Grid item xs={6} md={3}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <MDTypography variant='caption' color='text'>
                    เครดิตคงเหลือในบัญชี
                  </MDTypography>
                  {credit ? (
                    <>
                      <MDTypography variant='h4'>{num(credit.credit)}</MDTypography>
                      <MDTypography variant='caption' color='text'>
                        ≈ {baht(credit.est_baht)} บาท (ประมาณการ)
                      </MDTypography>
                    </>
                  ) : (
                    <MDButton size='small' variant='outlined' color='info' onClick={loadCredit}>
                      <Icon>sync</Icon>&nbsp;เช็คจากผู้ให้บริการ
                    </MDButton>
                  )}
                </Card>
              </Grid>
            </>
          )}

          {/* ── แยกตามโมเดล เดือนนี้ ─────────────────────────────────── */}
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 2 }}>
              <MDTypography variant='h6'>เดือนนี้แยกตามโมเดล</MDTypography>
              <Divider />
              <MDBox sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead sx={{ display: 'table-header-group' }}>
                    <TableRow>
                      <TableCell>โมเดล</TableCell>
                      <TableCell align='right'>ภาพ</TableCell>
                      <TableCell align='right'>ไม่สำเร็จ</TableCell>
                      <TableCell align='right'>เครดิต</TableCell>
                      <TableCell align='right'>≈ บาท</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byProvider.map((p) => (
                      <TableRow key={p.provider_code}>
                        <TableCell>{p.provider_name}</TableCell>
                        <TableCell align='right'>{num(p.images)}</TableCell>
                        <TableCell align='right'>
                          {Number(p.failed) > 0 ? (
                            <Chip size='small' color='error' label={num(p.failed)} />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='right'>{num(p.credits)}</TableCell>
                        <TableCell align='right'>{baht(p.credits * bahtPerCredit)}</TableCell>
                      </TableRow>
                    ))}
                    {byProvider.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>ยังไม่มีการใช้งานเดือนนี้</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </MDBox>
            </Card>
          </Grid>

          {/* ── ตั้งค่าคุมเงิน ───────────────────────────────────────── */}
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' alignItems='center' gap={1}>
                <MDTypography variant='h6'>ตั้งค่าคุมค่าใช้จ่าย</MDTypography>
                {saved.ai_kill_switch === 'Y' && (
                  <Chip size='small' color='error' label='AI ปิดทั้งร้าน' />
                )}
              </MDBox>
              <Divider />

              {canEdit ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <MDInput
                      label='โควตาภาพ/เดือน'
                      type='number'
                      fullWidth
                      value={settings.ai_monthly_quota}
                      onChange={(e) =>
                        setSettings({ ...settings, ai_monthly_quota: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText={
                        quotaZero(settings.ai_monthly_quota) ? '0 = ปิด AI ทั้งร้าน' : undefined
                      }
                      FormHelperTextProps={{ sx: { color: 'error.main', mx: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MDInput
                      label='งบ/เดือน (บาท)'
                      title='ไว้อ้างอิง ระบบไม่หยุดตามงบ'
                      type='number'
                      fullWidth
                      value={settings.ai_monthly_budget}
                      onChange={(e) =>
                        setSettings({ ...settings, ai_monthly_budget: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MDInput
                      label='เมื่อเกินโควตา'
                      select
                      fullWidth
                      value={settings.ai_over_quota}
                      onChange={(e) => setSettings({ ...settings, ai_over_quota: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value='STOP'>หยุดสร้างทันที</MenuItem>
                      <MenuItem value='WARN'>เตือนแต่สร้างต่อได้</MenuItem>
                    </MDInput>
                  </Grid>
                  <Grid item xs={6}>
                    <MDInput
                      label='หยุดฉุกเฉิน'
                      select
                      fullWidth
                      value={settings.ai_kill_switch}
                      onChange={(e) => setSettings({ ...settings, ai_kill_switch: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value='N'>ไม่หยุด · AI ใช้ได้</MenuItem>
                      <MenuItem value='Y'>หยุด AI ทั้งร้าน</MenuItem>
                    </MDInput>
                  </Grid>
                  <Grid item xs={12} display='flex' justifyContent='flex-end'>
                    <MDButton variant='gradient' color='success' onClick={requestSave}>
                      <Icon>save</Icon>&nbsp;{t(`element.save`)}
                    </MDButton>
                  </Grid>
                </Grid>
              ) : (
                <MDTypography variant='body2' color='text'>
                  {!loaded ? (
                    'บัญชีนี้ไม่มีสิทธิ์ดูการตั้งค่า AI'
                  ) : Number(saved.ai_monthly_quota) > 0 ? (
                    `โควตา ${num(saved.ai_monthly_quota)} ภาพ/เดือน · เกินโควตา${
                      saved.ai_over_quota === 'STOP' ? 'หยุดสร้าง' : 'เตือน'
                    }`
                  ) : (
                    <MDTypography component='span' variant='body2' color='error' fontWeight='bold'>
                      AI ปิดทั้งร้าน (โควตา 0)
                    </MDTypography>
                  )}{' '}
                  · บัญชีนี้ดูได้อย่างเดียว
                </MDTypography>
              )}
            </Card>
          </Grid>

          {canEdit ? (
            <Grid item xs={12}>
              <AiQuotaPanel token={app.token} onNotify={showNotify} />
            </Grid>
          ) : null}

          {/* ── ย้อนหลังรายเดือน ─────────────────────────────────────── */}
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDTypography variant='h6'>ย้อนหลังรายเดือน (12 เดือน)</MDTypography>
              <Divider />
              <MDBox sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead sx={{ display: 'table-header-group' }}>
                    <TableRow>
                      <TableCell>เดือน</TableCell>
                      <TableCell align='right'>ภาพ</TableCell>
                      <TableCell align='right'>ไม่สำเร็จ</TableCell>
                      <TableCell align='right'>เครดิต</TableCell>
                      <TableCell align='right'>≈ บาท (ประมาณการ)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyState === 'done' &&
                      history.map((row) => (
                        <TableRow key={row.month}>
                          <TableCell>{monthText(row.month)}</TableCell>
                          <TableCell align='right'>{num(row.images)}</TableCell>
                          <TableCell align='right'>
                            {Number(row.failed) > 0 ? num(row.failed) : '-'}
                          </TableCell>
                          <TableCell align='right'>{num(row.credits)}</TableCell>
                          <TableCell align='right'>{baht(row.est_baht)}</TableCell>
                        </TableRow>
                      ))}
                    {(historyState !== 'done' || history.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} aria-live='polite'>
                          {historyState === 'loading'
                            ? 'กำลังโหลดประวัติ…'
                            : historyState === 'error'
                            ? 'โหลดประวัติไม่สำเร็จ'
                            : 'ยังไม่มีประวัติการใช้งาน'}
                          {historyState === 'error' && (
                            <MDButton size='small' onClick={loadHistory}>
                              ลองอีกครั้ง
                            </MDButton>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth='xs'
        aria-labelledby='ai-off-title'
      >
        <DialogTitle id='ai-off-title'>ปิด AI ทั้งร้าน?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>
            {confirmOff.join(' + ')} = ทุกคนในร้านสร้างหรือแก้ภาพ AI ไม่ได้จนกว่าจะเปิดกลับ
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            onClick={() => setConfirmOpen(false)}
          >
            {t(`element.cancel`)}
          </MDButton>
          <MDButton variant='gradient' color='error' onClick={() => confirmOpen && saveSettings()}>
            ยืนยันปิด AI
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
      {notify}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </DashboardLayout>
  );
}

export default AiReport;
